import { useMemo } from 'react'

import { CustomizableDashboard, ModalContext } from '@domain-group/suit-theme'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { digObject } from '@domain-group/javascript-utils'

import type { PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCurrentUser from '@hooks/useCurrentUser'
import useUser from '@hooks/useUser'

import CreateOrEditOrderItemTaskModal from '@modals/CreateOrEditOrderItemTaskModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageCampaignPrioritySettingsModal from '@modals/ManageCampaignPrioritySettingsModal'
import MergeCampaignsModal from '@modals/MergeCampaignsModal'
import NewCampaignItemModal from '@modals/NewCampaignItemModal'

import type { ActivityModel, CampaignModel, OrderItemTaskModel } from '@models/types'

import Content from '../DashboardModules/Content'
import ContentSidebar from '../DashboardModules/ContentSidebar'
import PageLayout from '../PageLayout'

import { dashboardDefaultLayout, generateAvailableModules } from '../../utils/customizableDashboard'

const defaultState = {
  editingDashboard: false,
  showCreateOrEditOrderItemTaskModal: false,
  showManageActivityModal: false,
  showMergeCampaignsModal: false,
  showNewCampaignItemModal: false,
  showManageCampaignPrioritySettingsModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void
type UseUserPayload = ReturnType<typeof useUser>

const callbacks = (
  component: keyof typeof componentCallbacks,
  state: typeof defaultState,
  setState: SetState,
  userCallbacks: UseUserPayload['callbacks'],
) => {
  const { editingDashboard } = state
  const { saveUserDashboard } = userCallbacks

  const componentCallbacks = {
    CreateOrEditOrderItemTaskModal: {
      closeModal: () => setState({ showCreateOrEditOrderItemTaskModal: false }),
      completeOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createOrderItemTask: (payload: HandleCallbackActionParams) => (
        handleCallbackAction({ ...payload, shouldRedirect: true })
      ),
      deleteOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CustomizableDashboard: {
      saveDashboard: (dashboardKey: string, activeLayout: object) => saveUserDashboard(dashboardKey, activeLayout),
      toggleEditDashboard: () => setState({ editingDashboard: !editingDashboard }),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    ManageCampaignPrioritySettingsModal: {
      closeModal: () => setState({ showManageCampaignPrioritySettingsModal: false }),
      createPriorityCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    MergeCampaignsModal: {
      closeModal: () => setState({ showMergeCampaignsModal: false }),
    },
    NewCampaignItemModal: {
      closeModal: () => setState({ showNewCampaignItemModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

type PageContentProps = {
  campaign: CampaignModel,
}

const PageContent = (props: PageContentProps) => {
  const { campaign } = props

  const [state, setState] = useSetState(defaultState)
  const {
    editingDashboard,
    showCreateOrEditOrderItemTaskModal,
    showManageActivityModal,
    showManageCampaignPrioritySettingsModal,
    showMergeCampaignsModal,
    showNewCampaignItemModal,
  } = state

  const currentUserPayload = useCurrentUser()
  const { currentUser } = currentUserPayload
  const { callbacks: userCallbacks, user } = useUser(currentUser)

  const customizedLayout = digObject(user, 'options.custom_dashboards.campaign_manager')

  const availableModules = useMemo(() => generateAvailableModules(campaign), [campaign.id, user.id])

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateOrEditOrderItemTaskModal: (payload: { teamMember: Partial<OrderItemTaskModel> }) => {
        setModalData('CreateOrEditOrderItemTaskModal', payload)
        setState({ showCreateOrEditOrderItemTaskModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManageCampaignPrioritySettingsModal: (payload: { campaign: CampaignModel }) => {
        setModalData('ManageCampaignPrioritySettingsModal', payload)
        setState({ showManageCampaignPrioritySettingsModal: true })
      },
      showMergeCampaignsModal: (payload: { campaign: CampaignModel }) => {
        setModalData('MergeCampaignsModal', payload)
        setState({ showMergeCampaignsModal: true })
      },
      showNewCampaignItemModal: (payload: { campaign: CampaignModel }) => {
        setModalData('NewCampaignItemModal', payload)
        setState({ showNewCampaignItemModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageLayout activeTabBarItemKey="dashboard" campaign={campaign}>
          <CustomizableDashboard
            // @ts-expect-error TS2322
            availableModules={availableModules}
            callbacks={callbacks('CustomizableDashboard', state, setState, userCallbacks)}
            columns={[
              {
                id: 'main',
                component: <Content campaignId={campaign.id} />,
                title: 'Main',
              },
              {
                id: 'sidebar',
                component: (
                  // @ts-expect-error TS2739
                  <ContentSidebar
                    callbacks={{ customizeDashboard: () => setState({ editingDashboard: !editingDashboard }) }}
                    campaignId={campaign.id}
                  />
                ),
                title: 'Sidebar',
              },
            ]}
            customizedLayout={customizedLayout}
            dashboardKey="campaign_manager"
            dashboardReady={!!user.id}
            defaultLayout={dashboardDefaultLayout()}
            editing={editingDashboard}
          />
        </PageLayout>

        <CreateOrEditOrderItemTaskModal
          allowedTaskTypes={['clean', 'move', 'other', 'remove', 'repair']}
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditOrderItemTaskModal', state, setState, userCallbacks)}
          showModal={showCreateOrEditOrderItemTaskModal}
        />

        <ManageActivityModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageActivityModal', state, setState, userCallbacks)}
          showModal={showManageActivityModal}
        />

        <ManageCampaignPrioritySettingsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageCampaignPrioritySettingsModal', state, setState, userCallbacks)}
          showModal={showManageCampaignPrioritySettingsModal}
        />

        <MergeCampaignsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('MergeCampaignsModal', state, setState, userCallbacks)}
          showModal={showMergeCampaignsModal}
        />

        <NewCampaignItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('NewCampaignItemModal', state, setState, userCallbacks)}
          showModal={showNewCampaignItemModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default PageContent
